<template>
    <div class="payment-method-icon">
        <template v-if="hasBrand">
            <div
                class="pm-icon"
                :style="{ backgroundImage: `url('${cardIconByBrand(props.paymentInformation.brand!)}')` }"
            ></div>
            <div class="pm-digits"><span class="pm-bullets">••••</span>{{ props.paymentInformation.ccDigit }}</div>
        </template>

        <template v-else-if="hasActualPaymentMethod">
            <div
                class="pm-icon"
                :style="{ backgroundImage: `url('${props.paymentInformation.actualPaymentMethodIconUrl}')` }"
            ></div>
            <div class="pm-digits">{{ props.paymentInformation.actualPaymentMethodName }}</div>
        </template>

        <template v-else-if="hasPredefinedPaymentMethod">
            <div
                class="pm-icon"
                :class="{ wide: isStripeCheckout }"
                :style="{ backgroundImage: `url('${props.paymentInformation.predefinedPaymentMethodIconUrl}')` }"
            ></div>
            <div class="pm-digits">{{ props.paymentInformation.predefinedOnlinePaymentMethodName }}</div>
        </template>

        <template v-else-if="hasCustomPaymentMethod">
            <div
                class="pm-icon"
                :style="{ backgroundImage: `url('${props.paymentInformation.customPaymentMethodIcon}')` }"
            ></div>
            <div class="pm-digits">{{ props.paymentInformation.customPaymentMethodName }}</div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import type { IPaymentInformation } from "@/data/model/Customer";
    import { config } from "@/data/util/Config";
    import { Format } from "@/data/util/Format";
    import { computed } from "vue";

    const props = defineProps<{
        paymentInformation: IPaymentInformation;
    }>();

    function cardIconByBrand(brand: string): string | undefined {
        return Format.cardIconByBrand(brand);
    }

    const hasBrand = computed(() => {
        return props.paymentInformation.brand != null && props.paymentInformation.brand !== "";
    });

    const hasActualPaymentMethod = computed(() => {
        return (
            (props.paymentInformation.actualPaymentMethodName != null &&
                props.paymentInformation.actualPaymentMethodName !== "") ||
            (props.paymentInformation.actualPaymentMethodIconUrl != null &&
                props.paymentInformation.actualPaymentMethodIconUrl !== "")
        );
    });

    const hasPredefinedPaymentMethod = computed(() => {
        return (
            (props.paymentInformation.predefinedOnlinePaymentMethodName != null &&
                props.paymentInformation.predefinedOnlinePaymentMethodName !== "") ||
            (props.paymentInformation.predefinedPaymentMethodIconUrl != null &&
                props.paymentInformation.predefinedPaymentMethodIconUrl !== "")
        );
    });

    const isStripeCheckout = computed(() => {
        return props.paymentInformation.type === config.stripeCheckoutPaymentMethod;
    });

    const hasCustomPaymentMethod = computed(() => {
        return (
            (props.paymentInformation.customPaymentMethodName != null &&
                props.paymentInformation.customPaymentMethodName != "") ||
            (props.paymentInformation.customPaymentMethodIcon != null &&
                props.paymentInformation.customPaymentMethodIcon != "")
        );
    });
</script>

<style lang="scss">
    .payment-method-icon {
        .pm-icon {
            min-width: 26px;
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center center;
            margin: 2px 8px 2px 0px;
            display: inline-block;
            vertical-align: top;
            height: 18px;

            &.wide {
                min-width: 28px;
            }
        }

        .pm-digits {
            display: inline-block;
            margin-right: 8px;
            vertical-align: top;
            font-size: 14px;
            line-height: 21px;

            > .pm-bullets {
                letter-spacing: 1px;
                font-size: 10px;
                line-height: 21px;
                padding-right: 2px;
                display: inline-block;
                vertical-align: top;
            }
        }
    }
</style>
